import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import api, { BASE_URL } from "../../Utils/api";
import {
  Card, Table, Spinner, Row, Col, Badge, Button,
} from "react-bootstrap";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend,
} from "chart.js";
import {
  FaChartPie, FaPlus, FaFileAlt, FaExclamationTriangle,
  FaTimesCircle, FaCheckCircle, FaArrowRight, FaArchive, FaEnvelope,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import PageHeader from "../PageHeader/PageHeader";
import { GlobalContext } from "../../Utils/GlobalContext";
import DashboardCustomizados from "./DashboardCustomizados";
import DocumentosModal from "../Documentos/DocumentosModal";
import formatarData from "../../Utils/formatarData";
import calcDiasParaVcto from "../../Utils/calcDiasParaVcto";
import "../PaginaInicial/PaginaInicial.css";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


/* ─── KPI Card ─────────────────────────────────────────────────────────── */
function KpiCard({ titulo, valor, icone, cor, rodape, loading }) {
  return (
    <Card className="kpi-card h-100 border-0 shadow-sm">
      <div className="kpi-accent" style={{ backgroundColor: cor }} />
      <Card.Body className="d-flex align-items-center gap-3 p-3">
        <div
          className="kpi-icon-wrapper d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
          style={{ backgroundColor: `${cor}18`, width: 52, height: 52 }}
        >
          {React.cloneElement(icone, { size: 22, color: cor })}
        </div>
        <div className="min-w-0">
          <div className="kpi-label text-muted text-uppercase mb-1">{titulo}</div>
          {loading ? (
            <Spinner animation="border" size="sm" style={{ color: cor }} />
          ) : (
            <div className="kpi-valor fw-bold lh-1" style={{ color: cor }}>{valor}</div>
          )}
          {rodape && <div className="kpi-rodape text-muted mt-1">{rodape}</div>}
        </div>
      </Card.Body>
    </Card>
  );
}

/* ─── Painel de Alerta ─────────────────────────────────────────────────── */
function AlertaBanner({ kpis, loading, onVerDocumentos }) {
  if (loading) return null;
  if (kpis.vencidos === 0 && kpis.vencendo30 === 0) return null;

  return (
    <div
      className="dashboard-alert-banner alert alert-warning d-flex align-items-center justify-content-between gap-2 mb-4 py-2 px-3"
      role="alert"
    >
      <div className="dashboard-alert-content">
        <FaExclamationTriangle className="dashboard-alert-icon" />
        <div className="dashboard-alert-copy">
          <strong>Atenção:</strong>
          {kpis.vencidos > 0 && (
            <span>
              <span className="text-danger fw-bold">{kpis.vencidos}</span> documento
              {kpis.vencidos !== 1 ? "s" : ""} vencido{kpis.vencidos !== 1 ? "s" : ""}
              {kpis.vencendo30 > 0 ? " e " : "."}
            </span>
          )}
          {kpis.vencendo30 > 0 && (
            <span>
              <span className="fw-bold">{kpis.vencendo30}</span> vencendo em até 30 dias.
            </span>
          )}
        </div>
      </div>
      <Button
        variant="warning"
        size="sm"
        className="dashboard-alert-button flex-shrink-0 d-flex align-items-center gap-1 fw-600"
        onClick={onVerDocumentos}
      >
        Ver documentos <FaArrowRight size={12} />
      </Button>
    </div>
  );
}

/* ─── Componente principal ─────────────────────────────────────────────── */
function Estatisticas() {
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const userId     = globalData?.user_id     || localStorage.getItem("user_id");
  const token      = localStorage.getItem("authToken");
  const navigate   = useNavigate();

  // ── Estatísticas agregadas ──────────────────────────────────────────────────
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  // ── Limite de e-mails pelo remetente padrão ────────────────────────────────
  const [limiteEmailCompartilhado, setLimiteEmailCompartilhado] = useState(null);

  // ── Modal de documento ─────────────────────────────────────────────────────
  const [showDocumentoModal,  setShowDocumentoModal]  = useState(false);
  const [documentoParaModal,  setDocumentoParaModal]  = useState(null);

  // ── Tabs customizadas ──────────────────────────────────────────────────────
  const [activeTab,        setActiveTab]        = useState("padrao");
  const [customDashboards, setCustomDashboards] = useState([]);
  const [novoDashboardId,  setNovoDashboardId]  = useState(null);

  // Fetch estatísticas agregadas
  useEffect(() => {
    if (!entidadeID || !token) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    api
      .get(`${BASE_URL}/api/documentos/Estatisticas/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
        params:  { user_id: Number(userId) || userId },
        signal: controller.signal,
      })
      .then((res) => setEstatisticas(res.data?.data || null))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setError(err.response?.data?.message || "Erro ao carregar estatísticas.");
        setEstatisticas(null);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [entidadeID, token, userId]);

  // Fetch status do limite de e-mails pelo remetente padrão
  useEffect(() => {
    if (!entidadeID || !token) return;
    api
      .get(`${BASE_URL}/api/emailNotificacao/LimiteCompartilhado/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setLimiteEmailCompartilhado(res.data?.data || null))
      .catch(() => setLimiteEmailCompartilhado(null));
  }, [entidadeID, token]);

  // Fetch custom dashboards
  const fetchCustomDashboards = useCallback(async () => {
    if (!token || !entidadeID) return;
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/dashboardsCustomizados/Listar/${entidadeID}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomDashboards(Array.isArray(data.data) ? data.data : []);
    } catch {
      setCustomDashboards([]);
    }
  }, [token, entidadeID]);

  useEffect(() => { fetchCustomDashboards(); }, [fetchCustomDashboards]);

  const handleCriarDashboard = async () => {
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/dashboardsCustomizados/Criar/${entidadeID}`,
        { nome: "Novo Dashboard" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCustomDashboards();
      setActiveTab(String(data.data.id));
      setNovoDashboardId(data.data.id);
    } catch (err) {
      console.error("Erro ao criar dashboard:", err);
    }
  };

  const renderTabBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 16, borderBottom: "1px solid #dee2e6", paddingBottom: 8, flexWrap: "wrap" }}>
      <Button
        size="sm"
        variant={activeTab === "padrao" ? "primary" : "outline-secondary"}
        style={{ borderRadius: 20, fontSize: "0.8rem" }}
        onClick={() => setActiveTab("padrao")}
      >
        Padrão
      </Button>
      {customDashboards.map((dash) => (
        <Button
          key={dash.id}
          size="sm"
          variant={activeTab === String(dash.id) ? "primary" : "outline-secondary"}
          style={{ borderRadius: 20, fontSize: "0.8rem" }}
          onClick={() => setActiveTab(String(dash.id))}
        >
          {dash.nome}
        </Button>
      ))}
      <Button
        size="sm"
        variant="outline-success"
        style={{ borderRadius: 20, padding: "2px 10px", fontSize: "0.8rem" }}
        onClick={handleCriarDashboard}
        title="Criar novo dashboard customizado"
      >
        <FaPlus size={10} className="me-1" /> Novo
      </Button>
    </div>
  );

  /* ── KPIs ─────────────────────────────────────────────────────────────── */
  const kpis = useMemo(() => estatisticas?.kpis || {
    total: 0, vigentes: 0, arquivados: 0, vencidos: 0, vencendo30: 0, emDia: 0,
  }, [estatisticas]);

  /* ── Documentos críticos ─────────────────────────────────────────────── */
  const documentosCriticos = useMemo(() => estatisticas?.documentosCriticos || [], [estatisticas]);

  /* ── Chart: Doughnut – Status ─────────────────────────────────────── */
  const doughnutData = useMemo(() => ({
    labels: ["Em dia", "Vencendo (30d)", "Vencidos", "Arquivados"],
    datasets: [{
      data: [kpis.emDia, kpis.vencendo30, kpis.vencidos, kpis.arquivados],
      backgroundColor: ["#059669", "#f59e0b", "#dc3545", "#94a3b8"],
      borderColor:     ["#047857", "#d97706", "#bb2d3b", "#64748b"],
      borderWidth: 2, hoverOffset: 6,
    }],
  }), [kpis]);

  const doughnutOptions = {
    responsive: true, maintainAspectRatio: false, cutout: "65%",
    onClick: () => navigate("/Documentos"),
    onHover: (event) => { if (event?.native?.target) event.native.target.style.cursor = "pointer"; },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 12 }, padding: 14 } },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` } },
    },
  };

  /* ── Chart: Bar Horizontal – Tipos (top 6) ─────────────────────────── */
  const tiposData = useMemo(() => {
    const topTipos = estatisticas?.topTipos || [];
    return {
      labels: topTipos.map((t) => t.nome),
      datasets: [{
        label: "Documentos",
        data: topTipos.map((t) => t.total),
        backgroundColor: ["#0d6efd", "#059669", "#f59e0b", "#dc3545", "#6f42c1", "#0dcaf0"],
        borderRadius: 4,
      }],
    };
  }, [estatisticas]);

  const tiposOptions = {
    responsive: true, maintainAspectRatio: false, indexAxis: "y",
    onClick: () => navigate("/Documentos"),
    onHover: (event) => { if (event?.native?.target) event.native.target.style.cursor = "pointer"; },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.x} documentos` } },
    },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 }, grid: { display: false } },
      y: { grid: { display: false } },
    },
  };

  /* ── Chart: Bar – Vencimentos próximos 6 meses ─────────────────────── */
  const vencMesData = useMemo(() => {
    const now = new Date();
    const vencMap = {};
    (estatisticas?.vencimentosMes || []).forEach(({ ano, mes, total }) => {
      vencMap[`${ano}-${mes}`] = total;
    });
    const labels = [], counts = [], colors = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      labels.push(d.toLocaleString("pt-BR", { month: "short", year: "2-digit" }));
      counts.push(vencMap[`${d.getFullYear()}-${d.getMonth() + 1}`] || 0);
      colors.push(i === 0 ? "#dc3545" : i === 1 ? "#f59e0b" : "#0d6efd");
    }
    return {
      labels,
      datasets: [{ label: "Vencimentos", data: counts, backgroundColor: colors, borderRadius: 5 }],
    };
  }, [estatisticas]);

  const vencMesOptions = {
    responsive: true, maintainAspectRatio: false,
    onClick: () => navigate("/Documentos"),
    onHover: (event) => { if (event?.native?.target) event.native.target.style.cursor = "pointer"; },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y} documento(s)` } },
    },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { grid: { display: false } },
    },
  };

  /* ── Badge de dias ─────────────────────────────────────────────────── */
  const getDiasBadge = (dias) => {
    if (dias === null || dias === undefined) return <span className="text-muted small">—</span>;
    if (dias < 0)   return <Badge bg="danger">{dias} dias</Badge>;
    if (dias <= 7)  return <Badge bg="danger">{dias} dias</Badge>;
    if (dias <= 30) return <Badge bg="warning" text="dark">{dias} dias</Badge>;
    return <Badge bg="success">{dias} dias</Badge>;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader titulo="Estatísticas" path="/Estatisticas" />
      {renderTabBar()}

      {activeTab !== "padrao" ? (
        (() => {
          const dash = customDashboards.find((d) => String(d.id) === activeTab);
          return dash ? (
            <DashboardCustomizados
              key={dash.id}
              dashboard={dash}
              onUpdate={fetchCustomDashboards}
              autoRename={novoDashboardId === dash.id}
              onRenameComplete={() => setNovoDashboardId(null)}
              onDelete={(id) => {
                setCustomDashboards((prev) => prev.filter((d) => d.id !== id));
                setActiveTab("padrao");
              }}
            />
          ) : (
            <div className="text-muted text-center py-5">Dashboard não encontrado.</div>
          );
        })()
      ) : (
        <>
          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}

          <AlertaBanner
            kpis={kpis}
            loading={loading}
            onVerDocumentos={() => navigate("/Documentos")}
          />

          {/* KPI Cards */}
          <Row className="g-3 mb-4">
            <Col xs={12} sm={6} xl={3}>
              <KpiCard titulo="Total de Documentos" valor={kpis.total}
                icone={<FaFileAlt />} cor="#0d6efd"
                rodape={`${kpis.vigentes} vigente${kpis.vigentes !== 1 ? "s" : ""}`}
                loading={loading} />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <KpiCard titulo="Vigentes (em dia)" valor={kpis.emDia}
                icone={<FaCheckCircle />} cor="#059669"
                rodape="Vencimento > 30 dias" loading={loading} />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <KpiCard titulo="Vencendo em 30 dias" valor={kpis.vencendo30}
                icone={<FaExclamationTriangle />} cor="#f59e0b"
                rodape="Requer atenção" loading={loading} />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <KpiCard titulo="Vencidos" valor={kpis.vencidos}
                icone={<FaTimesCircle />} cor="#dc3545"
                rodape={`${kpis.arquivados} arquivado${kpis.arquivados !== 1 ? "s" : ""}`}
                loading={loading} />
            </Col>
            {limiteEmailCompartilhado && !limiteEmailCompartilhado.tem_email_proprio && (
              <Col xs={12} sm={6} xl={3}>
                <KpiCard
                  titulo="E-mails via remetente padrão"
                  valor={`${limiteEmailCompartilhado.usado}/${limiteEmailCompartilhado.limite}`}
                  icone={<FaEnvelope />}
                  cor={
                    limiteEmailCompartilhado.restante === 0
                      ? "#dc3545"
                      : limiteEmailCompartilhado.restante <= limiteEmailCompartilhado.limite * 0.2
                        ? "#f59e0b"
                        : "#6f42c1"
                  }
                  rodape={
                    limiteEmailCompartilhado.restante === 0
                      ? "Limite atingido — configure um SMTP próprio"
                      : `${limiteEmailCompartilhado.restante} restantes — configure um SMTP próprio`
                  }
                  loading={loading}
                />
              </Col>
            )}
          </Row>

          {/* Gráficos */}
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Header className="dashboard-card-header d-flex align-items-center">
                  <FaChartPie size={15} className="me-2 text-primary" />
                  Status dos Documentos
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  {loading ? (
                    <Spinner animation="border" variant="primary" />
                  ) : kpis.total === 0 ? (
                    <p className="text-muted small mb-0">Sem dados</p>
                  ) : (
                    <div style={{ height: 230, width: "100%" }}>
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Header className="dashboard-card-header d-flex align-items-center">
                  <FaArchive size={15} className="me-2 text-success" />
                  Documentos por Tipo
                </Card.Header>
                <Card.Body className="d-flex align-items-center justify-content-center">
                  {loading ? (
                    <Spinner animation="border" variant="primary" />
                  ) : tiposData.labels.length === 0 ? (
                    <p className="text-muted small mb-0">Sem dados</p>
                  ) : (
                    <div style={{ height: 230, width: "100%" }}>
                      <Bar data={tiposData} options={tiposOptions} />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Header className="dashboard-card-header d-flex align-items-center">
                  <FaExclamationTriangle size={15} className="me-2 text-warning" />
                  Vencimentos (próximos 6 meses)
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="d-flex justify-content-center align-items-center h-100">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <div style={{ height: 230 }}>
                      <Bar data={vencMesData} options={vencMesOptions} />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Documentos Críticos */}
          <Card className="shadow-sm border-0">
            <Card.Header className="dashboard-card-header d-flex align-items-center gap-2">
              <FaExclamationTriangle size={15} className="text-warning" />
              <span>Documentos Críticos</span>
              {!loading && kpis.vencidos + kpis.vencendo30 > 0 && (
                <Badge bg="danger" pill>{kpis.vencidos + kpis.vencendo30}</Badge>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : documentosCriticos.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaCheckCircle size={36} color="#059669" className="d-block mx-auto mb-2" />
                  <strong className="d-block text-success">Tudo em dia!</strong>
                  Nenhum documento crítico no momento.
                </div>
              ) : (
                <Table hover responsive size="sm" className="mb-0 docs-criticos-table">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}>ID</th>
                      <th>Empresa / Fornecedor</th>
                      <th>Tipo de Documento</th>
                      <th>Nº / Referência</th>
                      <th>Vencimento</th>
                      <th>Vence em</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentosCriticos.map((doc) => {
                      const dias = calcDiasParaVcto(doc.dt_vencimento);
                      return (
                        <tr
                          key={doc.id}
                          className={dias !== null && dias < 0 ? "table-danger" : "table-warning"}
                          style={{ cursor: "pointer" }}
                          onClick={() => { setDocumentoParaModal({ id: doc.id }); setShowDocumentoModal(true); }}
                          title="Clique para abrir o documento"
                        >
                          <td className="text-muted small fw-bold">{doc.id}</td>
                          <td>{doc.fornecedor || doc.empresa || "—"}</td>
                          <td>{doc.tipo_documento || "—"}</td>
                          <td>{doc.nr_contrato || "—"}</td>
                          <td>{formatarData(doc.dt_vencimento)}</td>
                          <td>{getDiasBadge(dias)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {showDocumentoModal && (
            <DocumentosModal
              show={showDocumentoModal}
              handleClose={() => { setShowDocumentoModal(false); setDocumentoParaModal(null); }}
              documento={documentoParaModal}
              fetchDocumentos={() => {}}
            />
          )}
        </>
      )}
    </div>
  );
}

export default Estatisticas;
